import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useChainContext } from './ChainContext';
import BlockchainService from '../services/blockchainService';

const WalletAccountContext = createContext(null);

/**
 * Cuenta del usuario conectado, compartida por todas las páginas de la wallet.
 * Se descarga una vez por sesión y cadena; refresh() la vuelve a pedir sin
 * borrar lo que hay en pantalla (sin parpadeos ni spinners de página).
 *
 * → { info, loading (solo la primera carga), refreshing, error, refresh(delayMs) }
 */
export const WalletAccountProvider = ({ children }) => {
  const { user, currentChain } = useChainContext();
  const [state, setState] = useState({ info: null, loading: false, refreshing: false, error: null });
  const name = user?.name;
  const reqId = useRef(0);

  const load = useCallback(async () => {
    if (!name) return;
    const id = ++reqId.current;
    setState((s) => ({ ...s, loading: !s.info, refreshing: !!s.info, error: null }));
    try {
      const info = await new BlockchainService(currentChain).fetchFromChain(`/get_account/${name}`);
      if (id !== reqId.current) return;
      if (!info?.account) throw new Error('Account not found');
      setState({ info, loading: false, refreshing: false, error: null });
    } catch (err) {
      if (id !== reqId.current) return;
      setState((s) => ({ ...s, loading: false, refreshing: false, error: err.message }));
    }
  }, [name, currentChain]);

  useEffect(() => {
    reqId.current++;
    setState({ info: null, loading: !!name, refreshing: false, error: null });
    if (name) load();
  }, [name, currentChain, load]);

  // Tras firmar, la cadena tarda un poco en reflejar el cambio
  const refresh = useCallback((delayMs = 0) => {
    if (delayMs > 0) setTimeout(load, delayMs);
    else load();
  }, [load]);

  return (
    <WalletAccountContext.Provider value={{ ...state, refresh }}>
      {children}
    </WalletAccountContext.Provider>
  );
};

export const useWalletAccount = () => useContext(WalletAccountContext);
